import React from 'react';
import { Text, TextVariant } from '@naf/text';
import { CarModelType } from '@appTypes/carModelType';
import S from '@styles/bilguide/StyledBilGuide';
import { CarSubListPlaceholder } from './CarSubListPlaceholder';
import { ElementImage, ElementInfo, ElementPrice, ListElement, PriceInfoWrapper, Wrapper } from './CarSubList.styles';
import { LazyImage } from '../../lazy-image/LazyImage';
import { numberWithSpaces } from '../../../lib';
import { toCloudinaryPath } from '../cloudinary/cloudinary';
import { formatDateWithMonthName } from '../../../utils/Dates';

interface Props {
  cars: any;
  isUpdating: boolean;
}

const GetTestedLabel = (latestTestDate: string, latestTestDateLabel: string) => {
  if (!latestTestDate) return null;
  const testDate = new Date(latestTestDate);
  const isFutureTest = testDate > new Date();
  return isFutureTest
    ? `Resultater kommer ${formatDateWithMonthName(latestTestDate)}`
    : `Sist testet ${latestTestDateLabel?.toLowerCase()}`;
};

const CarSubList = ({ cars, isUpdating }: Props) => {
  if (isUpdating && cars.length === 0) return <CarSubListPlaceholder key="1-link-list-placeholder" />;

  const modifyCloudinaryImageUrlToThumbnail = (path: string) => path.replace('/upload/v', '/upload/c_scale,w_330/v');

  return (
    <Wrapper>
      {cars.map((car: CarModelType) => {
        if (!car) return null;

        const {
          name,
          brandName,
          modelName,
          modelMinimumPrice_NOK,
          vehicleModelPictures,
          chassis,
          fuelCategory,
          minWltp_km,
          maxWltp_km,
          preferredPicture,
          latestTestDate,
          latestTestDateLabel,
        } = car;
        const displayName = name || `${brandName} ${modelName}`;
        const listImg = vehicleModelPictures?.find((pic: any) => pic.type === 'smallFront');
        const getCarRange = () => {
          if (!minWltp_km && maxWltp_km) {
            return `${maxWltp_km}`;
          }
          return minWltp_km && minWltp_km === maxWltp_km ? `${minWltp_km}` : `${minWltp_km}-${maxWltp_km}`;
        };
        const labelList = [...fuelCategory, ...chassis];

        const testedLabel = GetTestedLabel(latestTestDate, latestTestDateLabel);

        return isUpdating ? (
          <CarSubListPlaceholder key={`${car.id}-${car.slug}-link-list-placeholder`} />
        ) : (
          <ListElement
            key={`${car.id}-link-list-element`}
            to={{
              pathname: `/bilguiden/bilmodell/${car.slug}`,
            }}
          >
            <ElementImage>
              {preferredPicture?.srcSet?.jpgLarge ? (
                <LazyImage
                  actualSrc={modifyCloudinaryImageUrlToThumbnail(preferredPicture.srcSet.jpgLarge)}
                  displayName={displayName}
                  altText={preferredPicture.alt || preferredPicture.caption}
                />
              ) : (
                <LazyImage
                  actualSrc={toCloudinaryPath(listImg?.path, 'Thumbnail')}
                  displayName={displayName}
                  altText={listImg?.alternateText}
                />
              )}
            </ElementImage>
            <ElementInfo>
              <Text tag="h3" variant={TextVariant.Header3}>
                {displayName}
              </Text>
              <S.LabelList>
                {latestTestDateLabel && (
                  <S.LabelListElement key={`${car.id}-label-testDate`}>{testedLabel}</S.LabelListElement>
                )}
                {(minWltp_km > 0 || maxWltp_km > 0) && (
                  <S.LabelListElement key={`${car.id}-label-carRange`}>
                    {getCarRange()} km rekkevidde
                  </S.LabelListElement>
                )}
                {labelList.map((labelType: string) => (
                  <S.LabelListElement key={`${car.id}-label-${labelType}`}>{labelType}</S.LabelListElement>
                ))}
              </S.LabelList>
              <PriceInfoWrapper>
                {modelMinimumPrice_NOK > 0 && (
                  <ElementPrice>
                    fra {numberWithSpaces(modelMinimumPrice_NOK)}
                    ,-
                  </ElementPrice>
                )}
              </PriceInfoWrapper>
            </ElementInfo>
          </ListElement>
        );
      })}
    </Wrapper>
  );
};

export default CarSubList;
